import React from "react";
import { Link } from "react-router-dom";
import { Course, CourseStatus } from "../../academy/courses";
import LockedOverlay from "../../common/locked_content";
import { formatLearnersCount } from "../../../helpers";
import { useDispatch } from "react-redux";
import { openModal } from "../../../store/slices/modalSlice";
import { MODAL_NAME } from "../../../constants";
import { trackEvent } from "../../../analytics";

interface LearnCardProps {
  learnCourse: Course;
}

const LearnCard: React.FC<LearnCardProps> = ({ learnCourse }) => {
  const dispatch = useDispatch();
  return (
    <div className="w-full bg-white p-6 rounded-[12px] justify-center">
      <div className="flex items-center mb-2">
        <h2 className="text-xl font-bold text-gray-900">Learn</h2>
        <img
          src="/assets/money/investmentOptions.png"
          alt="investment options"
          className="ml-2 w-fit h-[11.5px] object-cover"
        />
      </div>
      <div className="max-w-[360px] w-full">
        <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
          <div className="relative">
            <img
              src={learnCourse.thumbnail}
              alt={learnCourse.title}
              className="w-full object-cover"
            />
            {learnCourse?.is_subscribed ? (
              <div className="absolute inset-0 flex transition-opacity duration-300">
                <div className="text-white px-4 py-2">
                  <p>
                    {learnCourse.lessons_count} Lessons •{" "}
                    {Math.floor(learnCourse.content_duration / 60)}h{" "}
                    {learnCourse.content_duration % 60}m
                  </p>
                  <h3 className="text-[14px] leading-[28px] font-bold w-3/4">
                    {learnCourse.title}
                  </h3>
                </div>
              </div>
            ) : (
              <LockedOverlay blur={false} />
            )}
          </div>
          <div className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-gray-800 mb-2">
                  {learnCourse.title}
                </h3>
                <div className="flex items-center">
                  <div
                    className={`flex -space-x-4 rtl:space-x-reverse float-left ${
                      learnCourse?.footer?.learners?.count === 0 && "hidden"
                    }`}
                  >
                    {/* Profile images */}
                    {learnCourse?.footer?.learners?.profile_pics?.map(
                      (profilePic: string, index: number) => (
                        <div
                          key={index}
                          className="w-8 h-8 border-2 border-[#EBEBEB] rounded-full overflow-hidden relative"
                        >
                          <img
                            className="w-full h-full absolute inset-0 object-cover"
                            src={profilePic}
                            alt="Profile"
                          />
                        </div>
                      ),
                    )}
                  </div>
                  <p className="ml-2 text-sm text-gray-800 font-semibold">
                    {formatLearnersCount(
                      learnCourse?.footer?.learners?.count || 0,
                    )}
                    + Learners
                  </p>
                </div>
              </div>

              {learnCourse.is_subscribed ? (
                <button className="bg-blue-500 text-white px-3 py-1.5 rounded-full text-xs hover:bg-blue-600 transition-colors duration-300 font-semibold">
                  {learnCourse?.footer?.course_status === "COMPLETED" ? (
                    <div className="flex gap-2 items-center">
                      <p>Completed</p>
                    </div>
                  ) : (
                    <Link
                      to={`/academy/courses/${learnCourse.course_id}`}
                      onClick={() => {
                        trackEvent("tax_cta_clicked", {
                          cta_for: "tax_planning_course",
                        });
                      }}
                    >
                      {CourseStatus[learnCourse?.footer?.course_status]}
                    </Link>
                  )}
                </button>
              ) : (
                <button
                  className="bg-blue-500 text-white px-3 py-1.5 rounded-full text-xs hover:bg-blue-600 transition-colors duration-300"
                  onClick={() => {
                    dispatch(
                      openModal({
                        modalName: MODAL_NAME.BECOME_MEMBER,
                      }),
                    );
                  }}
                >
                  {CourseStatus[learnCourse?.footer?.course_status]}
                </button>
              )}
            </div>
            <div className="flex justify-between items-center mt-2 ">
              <div className="w-[140px] bg-gray-700 rounded-lg h-3">
                <div
                  className="bg-gradient-text h-3 rounded-lg"
                  style={{
                    width:
                      `${learnCourse?.footer?.progress?.completion_percentage}%` ||
                      0,
                  }}
                ></div>
              </div>
              <p className="text-xs font-semibold">
                {learnCourse?.footer?.progress?.completion_percentage || 0}%
                Complete
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LearnCard;
